import { useState, useEffect } from 'react';
import { cgGet } from '../utils/coingecko';

export interface CoinDetail {
  marketCapRank: number;
  marketCap: number;
  volume24h: number;
  circulatingSupply: number;
  totalSupply: number | null;
  maxSupply: number | null;
  high24h: number;
  low24h: number;
  ath: number;
  athDate: string;
  athChangePercent: number;
  atl: number;
  atlDate: string;
  atlChangePercent: number;
  currentPrice: number;
  priceChange24h: number;
  priceChangePercent24h: number;
  // Metadata for the Coin's Info tab — from the same /coins/{id} response.
  description: string;        // raw HTML-ish text (sanitize before rendering)
  image: string;             // image.large
  categories: string[];      // non-empty categories
  genesisDate: string | null;
  homepage: string[];        // links.homepage (non-empty)
}

export interface CoinDetailResult {
  detail: CoinDetail | null;
  loading: boolean;
  error: string | null;
}

// 5-minute in-memory cache
const cache = new Map<string, { data: CoinDetail; ts: number }>();
const TTL = 5 * 60 * 1000;

export function useCoinDetail(coinId: string): CoinDetailResult {
  const [detail, setDetail]   = useState<CoinDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState<string | null>(null);

  useEffect(() => {
    if (!coinId) { setLoading(false); return; }

    const cached = cache.get(coinId);
    if (cached && Date.now() - cached.ts < TTL) {
      setDetail(cached.data);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    cgGet(`/coins/${coinId}`, {
      params: {
        localization:     false,
        tickers:          false,
        market_data:      true,
        community_data:   false,
        developer_data:   false,
        sparkline:        false,
      },
      timeout: 15_000,
    })
      .then(({ data }) => {
        if (cancelled) return;
        const md = (data as any).market_data ?? {};
        const links = (data as any).links ?? {};
        const cleanList = (arr: unknown): string[] =>
          Array.isArray(arr) ? (arr as unknown[]).filter((u): u is string => typeof u === 'string' && u.trim() !== '') : [];
        const parsed: CoinDetail = {
          marketCapRank:        (data as any).market_cap_rank         ?? 0,
          marketCap:            md.market_cap?.usd           ?? 0,
          volume24h:            md.total_volume?.usd         ?? 0,
          circulatingSupply:    md.circulating_supply        ?? 0,
          totalSupply:          md.total_supply              ?? null,
          maxSupply:            md.max_supply                ?? null,
          high24h:              md.high_24h?.usd             ?? 0,
          low24h:               md.low_24h?.usd              ?? 0,
          ath:                  md.ath?.usd                  ?? 0,
          athDate:              md.ath_date?.usd             ?? '',
          athChangePercent:     md.ath_change_percentage?.usd ?? 0,
          atl:                  md.atl?.usd                  ?? 0,
          atlDate:              md.atl_date?.usd             ?? '',
          atlChangePercent:     md.atl_change_percentage?.usd ?? 0,
          currentPrice:         md.current_price?.usd        ?? 0,
          priceChange24h:       md.price_change_24h          ?? 0,
          priceChangePercent24h: md.price_change_percentage_24h ?? 0,
          description:          (data as any).description?.en ?? '',
          image:               (data as any).image?.large    ?? '',
          categories:          cleanList((data as any).categories),
          genesisDate:         (data as any).genesis_date    ?? null,
          homepage:            cleanList(links.homepage),
        };
        cache.set(coinId, { data: parsed, ts: Date.now() });
        setDetail(parsed);
        setLoading(false);
      })
      .catch(() => {
        if (!cancelled) {
          setError('Failed to load coin details.');
          setLoading(false);
        }
      });

    return () => { cancelled = true; };
  }, [coinId]);

  return { detail, loading, error };
}
